import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../lib/api';
import { Zap, Mail, Lock, User, ArrowRight, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const Signup = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/auth/register', { email, password, name });
      if (response.data.status === 'pending') {
        setRegistrationSuccess(true);
      } else {
        toast.success('Account created! Welcome to CORE');
        navigate('/login');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show success message if registration is pending
  if (registrationSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-hero px-4">
        <div className="auth-card animate-slideUp text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Manrope' }}>
            Registration Successful!
          </h1>
          
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 my-6">
            <p className="text-amber-800 font-medium mb-2">Account Pending Approval</p>
            <p className="text-amber-700 text-sm">
              Your account has been created but requires admin approval before you can log in.
              Please contact your administrator or wait for approval.
            </p>
          </div>
          
          <p className="text-gray-600 text-sm mb-6">
            You will be able to access CORE once your account is approved.
          </p>
          
          <Button 
            onClick={() => navigate('/login')}
            className="w-full h-12 bg-[#4A9CC7] hover:bg-[#3A8CB7] text-white rounded-xl font-medium"
            data-testid="back-to-login-btn"
          >
            Back to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center gradient-hero px-4">
      <div className="auth-card animate-slideUp">
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-lg bg-[#4A9CC7] flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <span className="font-bold text-2xl text-gray-900" style={{ fontFamily: 'Manrope' }}>CORE</span>
        </div>
        
        <h1 className="text-2xl font-bold text-center text-gray-900 mb-2" style={{ fontFamily: 'Manrope' }}>
          Create Your Account
        </h1>
        <p className="text-center text-gray-600 mb-8">
          Start your productivity journey today
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-gray-700">Full Name</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="pl-10 h-12 rounded-xl border-gray-200 focus:border-[#4A9CC7] focus:ring-[#4A9CC7]"
                data-testid="signup-name-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-700">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 h-12 rounded-xl border-gray-200 focus:border-[#4A9CC7] focus:ring-[#4A9CC7]"
                data-testid="signup-email-input"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password" className="text-gray-700">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 h-12 rounded-xl border-gray-200 focus:border-[#4A9CC7] focus:ring-[#4A9CC7]"
                data-testid="signup-password-input"
              />
            </div>
            <p className="text-xs text-gray-500">At least 6 characters</p>
          </div>

          <Button 
            type="submit" 
            disabled={loading}
            className="w-full h-12 bg-[#4A9CC7] hover:bg-[#3A8CB7] text-white rounded-xl font-medium"
            data-testid="signup-submit-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>Create Account <ArrowRight className="w-5 h-5 ml-2" /></>
            )}
          </Button>
        </form>

        <p className="text-center text-gray-600 mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-[#4A9CC7] font-medium hover:underline" data-testid="login-link">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Signup;
